import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator,
} from "@/components/ui/command";
import { Ticket, Users, BookOpen, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ tickets: [], contacts: [], kb: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);

  // Cmd+K / Ctrl+K toggle
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Debounced search
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 2) {
      setResults({ tickets: [], contacts: [], kb: [] });
      return;
    }
    setLoading(true);
    timer.current = setTimeout(() => {
      api.get("/search", { params: { q: q.trim(), limit: 6 } })
        .then((r) => setResults(r.data))
        .catch(() => setResults({ tickets: [], contacts: [], kb: [] }))
        .finally(() => setLoading(false));
    }, 220);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  const go = useCallback((path) => { setOpen(false); setQ(""); navigate(path); }, [navigate]);

  const empty = useMemo(() =>
    !loading && q.trim().length >= 2 &&
    results.tickets.length === 0 && results.contacts.length === 0 && results.kb.length === 0,
  [loading, q, results]);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="border-slate-200 text-slate-500 font-normal w-72 justify-between hidden md:flex"
        data-testid="global-search-trigger"
      >
        <span className="flex items-center gap-2">
          <Search className="w-3.5 h-3.5" />
          Search tickets, contacts, KB...
        </span>
        <kbd className="text-[10px] font-mono bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">⌘K</kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search by ticket code, customer name, phone, KB title..."
          value={q}
          onValueChange={setQ}
          data-testid="global-search-input"
        />
        <CommandList>
          {q.trim().length < 2 && (
            <div className="py-10 text-center text-slate-400 text-xs">
              Type at least 2 characters to search across tickets, contacts and the knowledge base.
            </div>
          )}
          {loading && q.trim().length >= 2 && (
            <div className="py-6 text-center text-slate-400 text-xs">Searching...</div>
          )}
          {empty && <CommandEmpty>No results for "{q}"</CommandEmpty>}

          {results.tickets.length > 0 && (
            <>
              <CommandGroup heading="Tickets">
                {results.tickets.map((t) => (
                  <CommandItem
                    key={t.id}
                    value={`ticket-${t.id}-${t.code}-${t.subject}`}
                    onSelect={() => go(`/tickets/${t.id}`)}
                    data-testid={`gs-ticket-${t.code}`}
                  >
                    <Ticket className="w-3.5 h-3.5 text-indigo-500" />
                    <span className="font-mono text-xs text-slate-500 w-16">{t.code}</span>
                    <span className="truncate flex-1">{t.subject}</span>
                    <span className="text-[10px] text-slate-400">{t.customer_name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {results.contacts.length > 0 && (
            <>
              <CommandGroup heading="Contacts">
                {results.contacts.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={`contact-${c.id}-${c.name}`}
                    onSelect={() => go("/contacts")}
                    data-testid={`gs-contact-${c.id}`}
                  >
                    <Users className="w-3.5 h-3.5 text-emerald-500" />
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="text-[10px] text-slate-400 font-mono">{c.phone || c.email}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {results.kb.length > 0 && (
            <CommandGroup heading="Knowledge base">
              {results.kb.map((a) => (
                <CommandItem
                  key={a.id}
                  value={`kb-${a.id}-${a.title}`}
                  onSelect={() => go("/knowledge-base")}
                  data-testid={`gs-kb-${a.id}`}
                >
                  <BookOpen className="w-3.5 h-3.5 text-sky-500" />
                  <span className="flex-1 truncate">{a.title}</span>
                  <span className="text-[10px] text-slate-400">{a.category}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
